// =====================================================================
// src/components/intl-acca/IntlAccaSlipCard.tsx
// =====================================================================
// The primary user-facing surface of International Acca (PRD §2.3).
// Receives the IntlAccaSlipWithLegs shape; the host resolves legs from
// public.intl_acca_slip_legs by slip_id.
//
// HONEST states this component MUST handle:
//   - slip.total_odds === null      → "Computing…" cell
//   - slip.clv_score_final === null → "Awaits closes" cell (NOT zero)
//   - any leg.closing_price === null → leg row shows "AWAIT PINNACLE"
//
// The host page is responsible for:
//   - resolving match_id to a human-readable home-vs-away label
//   - tier-gating (wrap entire card in <TierLockedTeaser> for free tier)
//   - the "Share Slip" CTA wiring (server-renders the share card HTML
//     under public/share-cards/intl-acca-slip.html from slip_id alone)

import type { IntlAccaSlipWithLegs } from "../../db/intl-acca-types";
import { formatOdds, formatClvPp } from "../../db/intl-acca-types";
import { IntlAccaLegRow } from "./IntlAccaLegRow";

export interface IntlAccaSlipCardProps {
  slip: IntlAccaSlipWithLegs;
  /** Map of match_id → display label (host-supplied; null/missing falls back to match_id). */
  matchLabels?: Record<string, string>;
  /** Wired by the host to the share-card endpoint. */
  onShare?: () => void;
  /** Wired by the host to the bookmaker-handoff flow (Stake / 1xBet / etc). */
  onPlace?: () => void;
}

const SLIP_LABEL: Record<IntlAccaSlipWithLegs["slip_type"], string> = {
  FOCUS: "Focus Acca",
  VALUE: "Value Acca",
  POWER: "Power Acca",
};

const SLIP_LEG_COUNT_HINT: Record<IntlAccaSlipWithLegs["slip_type"], string> = {
  FOCUS: "3-leg",
  VALUE: "5-leg",
  POWER: "10-leg",
};

export function IntlAccaSlipCard({
  slip,
  matchLabels,
  onShare,
  onPlace,
}: IntlAccaSlipCardProps): JSX.Element {
  const legs = slip.legs;
  const audited = legs.filter((l) => l.clv_pp !== null).length;
  const allUnsettled = audited === 0;

  return (
    <article className="ia-slip-card" aria-labelledby={`slip-${slip.id}-title`}>
      {/* HEAD ROW */}
      <header
        style={{
          padding: "var(--sz-space-5) var(--sz-space-5) var(--sz-space-4)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 24,
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <div>
          <div className="ia-eyebrow">
            {SLIP_LABEL[slip.slip_type]} · {SLIP_LEG_COUNT_HINT[slip.slip_type]} ·{" "}
            <span className="ia-mono" style={{ letterSpacing: "0.14em" }}>
              {slip.id.slice(0, 8)}
            </span>
          </div>
          <h2
            id={`slip-${slip.id}-title`}
            className="ia-title"
            style={{ fontSize: "var(--sz-head-md)", marginTop: 12 }}
          >
            {legs.length} legs · <em>{formatOdds(slip.total_odds)}× combined.</em>
          </h2>
        </div>

        {/* Total odds + slip-level CLV — both nullable */}
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div className="ia-metric-label">SLIP CLV</div>
          {slip.clv_score_final === null ? (
            <div
              className="ia-mono"
              style={{
                fontSize: "var(--sz-mono-md)",
                letterSpacing: "0.18em",
                color: "var(--text-tertiary)",
                textTransform: "uppercase",
                marginTop: 6,
              }}
            >
              Awaits closes
            </div>
          ) : (
            <div
              className="ia-mono"
              style={{
                fontSize: "var(--sz-head-sm)",
                color: "var(--accent-text)",
                marginTop: 4,
              }}
            >
              {formatClvPp(slip.clv_score_final)}
            </div>
          )}
          <div className="ia-metric-sub">
            {audited}/{legs.length} legs audited
          </div>
        </div>
      </header>

      {/* LEGS */}
      <div role="list">
        {legs.length === 0 ? (
          <div style={{ padding: "var(--sz-space-6)" }}>
            <div className="ia-empty-eyebrow">No legs</div>
            <p className="ia-empty-body">
              This slip exists but has no legs persisted. Wait for the
              orchestrator to publish, or check the slip-build job.
            </p>
          </div>
        ) : (
          legs.map((leg, i) => (
            <div role="listitem" key={leg.id}>
              <IntlAccaLegRow
                index={i + 1}
                leg={leg}
                matchLabel={matchLabels?.[leg.match_id]}
              />
            </div>
          ))
        )}
      </div>

      {/* FOOT */}
      <footer
        style={{
          padding: "var(--sz-space-4) var(--sz-space-5)",
          borderTop: "1px solid var(--border-faint)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--bg-elev-2)",
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <span
            className={
              slip.status === "ACTIVE"
                ? "ia-pill ia-pill-amber"
                : slip.status === "WON"
                  ? "ia-pill ia-pill-pos"
                  : slip.status === "LOST"
                    ? "ia-pill ia-pill-neg"
                    : "ia-pill ia-pill-ghost"
            }
          >
            {slip.status}
          </span>
          {allUnsettled ? (
            <span className="ia-clv-cell-empty">CLV settles at kickoff</span>
          ) : (
            <span
              className="ia-mono"
              style={{ fontSize: "var(--sz-mono-sm)", color: "var(--text-secondary)" }}
            >
              CLV vs Pinnacle close · PRD §9.1
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {onShare ? (
            <button type="button" onClick={onShare} className="ia-btn-ghost" style={ghostBtn}>
              Share Slip
            </button>
          ) : null}
          {onPlace ? (
            <button type="button" onClick={onPlace} style={primaryBtn}>
              Place on Stake →
            </button>
          ) : null}
        </div>
      </footer>
    </article>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--sz-text-inverse)",
  border: "none",
  borderRadius: "var(--sz-radius-md)",
  padding: "10px 18px",
  fontFamily: "var(--sz-font-mono)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: "var(--sz-radius-md)",
  padding: "10px 18px",
  fontFamily: "var(--sz-font-mono)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  cursor: "pointer",
};
